/**
 * Per-customer health scoring for the super CRM.
 *
 * Where `score.ts` grades a *lead's* heat, this grades a *live customer's*
 * health — how much risk an active account is carrying right now. Like the
 * lead score it is computed on demand from the customer's current signals
 * (never stored, so it can't drift out of sync with the record), and returns
 * a 0–100 number, a coarse `band` (at-risk / watch / healthy) for
 * color-coding, and a transparent breakdown of the factors so a CS owner can
 * see *exactly why* an account is slipping.
 *
 * Health starts from a healthy baseline and is debited by real risk signals,
 * each pulled straight from the customer's own record (no invented numbers):
 *   - lifecycle status (paused / churned),
 *   - past-due billing milestones (real overdue, unpaid invoices),
 *   - renewal proximity (a renewal bearing down — or already lapsed — is risk),
 *   - touch recency (silence is the loudest churn signal),
 *   - blocked / overdue deliverables,
 *   - overdue follow-up,
 *   - overdue open tasks tied to the account (optional — passed in).
 *
 * Higher = healthier. Only customers are scored; calling on a non-customer
 * returns a neutral score with an explanatory factor rather than guessing.
 */

import type { Person, ProjectTask } from '../db/types';
import { lastTouchAt, isFollowUpOverdue } from './score';

export type HealthBand = 'at-risk' | 'watch' | 'healthy';

export interface HealthFactor {
  label: string;
  /** Signed contribution to the score (debits are negative). */
  points: number;
}

export interface CustomerHealth {
  /** Clamped 0–100. Higher is healthier. */
  score: number;
  band: HealthBand;
  factors: HealthFactor[];
}

export interface HealthInput {
  /** All project tasks; the scorer filters to those tied to this customer. */
  tasks?: ProjectTask[];
  /** Override "now" for deterministic testing. */
  now?: number;
}

const DAY_MS = 86_400_000;

/** A customer's health begins here and is debited by risk signals. */
const HEALTHY_BASELINE = 100;

function daysSince(iso: string | undefined, now: number): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, (now - t) / DAY_MS);
}

/** Whole days until `iso` (negative if already past), or undefined. */
function daysUntil(iso: string | undefined, now: number): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return Math.round((t - now) / DAY_MS);
}

function bandFor(score: number): HealthBand {
  if (score >= 70) return 'healthy';
  if (score >= 40) return 'watch';
  return 'at-risk';
}

/**
 * Penalty for overdue, unpaid billing milestones — the most concrete risk a
 * live account carries. Mirrors the scanner's "Billing past due" rule (date
 * in the past, status not paid). Bounded so a long arrears list can't sink the
 * score past every other signal on its own.
 */
function billingPenalty(person: Person, now: number): HealthFactor | undefined {
  const overdue = (person.billingDates ?? []).filter((bd) => {
    const due = daysUntil(bd.date, now);
    if (due === undefined || due >= 0) return false;
    return (bd.status ?? 'scheduled') !== 'paid';
  });
  if (overdue.length === 0) return undefined;
  // -15 per overdue invoice, +10 extra for any aged 30d+, capped at -40.
  const aged = overdue.some((bd) => (daysUntil(bd.date, now) ?? 0) <= -30);
  const points = Math.max(-40, overdue.length * -15 + (aged ? -10 : 0));
  return {
    label: `Past-due billing · ${overdue.length} invoice${overdue.length > 1 ? 's' : ''}`,
    points,
  };
}

/** Risk from a renewal that is bearing down or has already lapsed. */
function renewalPenalty(person: Person, now: number): HealthFactor | undefined {
  const days = daysUntil(person.renewalDate, now);
  if (days === undefined) return undefined;
  if (days < 0) return { label: `Renewal lapsed ${Math.abs(days)}d ago`, points: -20 };
  if (days <= 14) return { label: `Renewal in ${days}d`, points: -15 };
  if (days <= 30) return { label: `Renewal in ${days}d`, points: -10 };
  return undefined;
}

/** Recency debit from the freshest touch — silence is the churn signal. */
function recencyPenalty(person: Person, now: number): HealthFactor {
  const days = daysSince(lastTouchAt(person), now);
  if (days === undefined) return { label: 'No touch logged', points: -15 };
  if (days <= 7) return { label: 'Touched this week', points: 0 };
  if (days <= 30) return { label: 'Touched this month', points: -6 };
  if (days <= 60) return { label: `Quiet ${Math.round(days)}d`, points: -15 };
  if (days <= 90) return { label: `Silent ${Math.round(days)}d`, points: -20 };
  return { label: `Silent ${Math.round(days)}d`, points: -25 };
}

/** Penalty for blocked deliverables (a delivery that has stalled). */
function blockedDeliverablesPenalty(person: Person): HealthFactor | undefined {
  const blocked = (person.deliverables ?? []).filter((d) => d.status === 'blocked');
  if (blocked.length === 0) return undefined;
  return {
    label: `Blocked deliverable${blocked.length > 1 ? 's' : ''} · ${blocked.length}`,
    points: Math.max(-24, blocked.length * -12),
  };
}

/** Penalty for deliverables past their due date and not yet delivered. */
function overdueDeliverablesPenalty(person: Person, now: number): HealthFactor | undefined {
  const overdue = (person.deliverables ?? []).filter((d) => {
    if (d.status === 'delivered') return false;
    const due = daysUntil(d.dueDate, now);
    return due !== undefined && due < 0;
  });
  if (overdue.length === 0) return undefined;
  return {
    label: `Overdue deliverable${overdue.length > 1 ? 's' : ''} · ${overdue.length}`,
    points: Math.max(-16, overdue.length * -8),
  };
}

/** Penalty for open tasks tied to this customer that are past their due date. */
function overdueTasksPenalty(person: Person, tasks: ProjectTask[], now: number): HealthFactor | undefined {
  const overdue = tasks.filter((t) => {
    if (t.personId !== person.id) return false;
    if (t.status === 'done') return false;
    const due = daysUntil(t.dueDate ?? undefined, now);
    return due !== undefined && due < 0;
  });
  if (overdue.length === 0) return undefined;
  return {
    label: `Overdue task${overdue.length > 1 ? 's' : ''} · ${overdue.length}`,
    points: Math.max(-15, overdue.length * -5),
  };
}

/**
 * Score a single customer's health. Pass `tasks` to fold overdue account
 * tasks into the score; omit it to score from the customer record alone.
 */
export function scoreCustomerHealth(person: Person, input: HealthInput = {}): CustomerHealth {
  const now = input.now ?? Date.now();
  const tasks = input.tasks ?? [];

  if (person.category !== 'customer') {
    return {
      score: 50,
      band: 'watch',
      factors: [{ label: 'Not a customer — health N/A', points: 0 }],
    };
  }

  const factors: HealthFactor[] = [];
  let score = HEALTHY_BASELINE;

  // Churned accounts aren't live — pin them at the floor, but still explain.
  if (person.status === 'churned') {
    return {
      score: 5,
      band: 'at-risk',
      factors: [{ label: 'Status: churned', points: -95 }],
    };
  }

  if (person.status === 'paused') {
    score -= 25;
    factors.push({ label: 'Status: paused', points: -25 });
  }

  const debits = [
    billingPenalty(person, now),
    renewalPenalty(person, now),
    recencyPenalty(person, now),
    blockedDeliverablesPenalty(person),
    overdueDeliverablesPenalty(person, now),
    overdueTasksPenalty(person, tasks, now),
  ];
  for (const f of debits) {
    if (f && f.points !== 0) {
      score += f.points;
      factors.push(f);
    }
  }

  if (isFollowUpOverdue(person)) {
    score -= 8;
    factors.push({ label: 'Follow-up overdue', points: -8 });
  }

  score = Math.max(0, Math.min(100, Math.round(score)));
  return { score, band: bandFor(score), factors };
}

/**
 * Rank every live customer in `people` by health, riskiest first. Churned
 * accounts are excluded by default (they're no longer "live"); pass
 * `includeChurned` to keep them. Each entry pairs the person with their score.
 */
export function rankCustomersByRisk(
  people: Person[],
  input: HealthInput & { includeChurned?: boolean } = {},
): { person: Person; health: CustomerHealth }[] {
  return people
    .filter((p) => p.category === 'customer')
    .filter((p) => input.includeChurned || p.status !== 'churned')
    .map((person) => ({ person, health: scoreCustomerHealth(person, input) }))
    .sort((a, b) => a.health.score - b.health.score);
}
